import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { ChevronDown } from 'react-feather'
import EnergyEmissionsTable from './EnergyEmissionsTable'
import { ProjectFragment } from '../../../generated/graphql'
import { SelectedVersion } from '../../layout/VersionsSelector'
import { mapVersionToCalculatedVersion } from '../../../calculators/use-calculator'
import { getEmissionModel } from '../../../emission-coefficients/emission-coefficients'
import FixedEmissionsTable from './FixedEmissionsTable'
import { EmissionWithVersionId, TableType } from './carbon-footprint-table-util'
import {
  emissionUnit,
  formatEmissionPerYear,
  formatLargeEmission,
  largeEmissionUnit,
} from '../../../util/formatters'
import { isNotNull } from '../../../util/array-utility'
import { EnergyCalculationMissing } from '../../common/EnergyCalculationMissing'

type Props = {
  project: ProjectFragment
  selectedVersions: SelectedVersion[]
  createPrefilledEmission: (
    emission: EmissionWithVersionId,
    newEmissionVersionId: string,
    buildingName: string,
  ) => void
  updateEmission: (emission: EmissionWithVersionId) => void
}

export default function CarbonFootprintTables({
  project,
  selectedVersions,
  createPrefilledEmission,
  updateEmission,
}: Props): React.ReactElement {
  const { t } = useTranslation()

  const calculatedVersions = selectedVersions.map((version) =>
    mapVersionToCalculatedVersion(version, version.energyCalculator),
  )
  if (calculatedVersions.some((version) => version === null)) {
    return <EnergyCalculationMissing />
  }
  const versions = calculatedVersions.filter(isNotNull)

  const emissionModel = getEmissionModel(project)

  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ChevronDown />}>
          <Typography variant="h6">
            {t('carbonFootprint.table.firstYearEmissions')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <EnergyEmissionsTable
            versions={versions}
            emissionModel={emissionModel}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion data-cy="one-time-emission-accordion">
        <AccordionSummary expandIcon={<ChevronDown />}>
          <Typography variant="h6">
            {t('carbonFootprint.table.oneTime')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails data-cy="one-time-emissions">
          <FixedEmissionsTable
            project={project}
            selectedVersions={selectedVersions}
            tableType={TableType.OneTime}
            formatter={formatLargeEmission}
            unit={largeEmissionUnit}
            createPrefilledEmission={createPrefilledEmission}
            updateEmission={updateEmission}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion data-cy="annual-emission-accordion">
        <AccordionSummary expandIcon={<ChevronDown />}>
          <Typography variant="h6">
            {t('carbonFootprint.table.annual')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FixedEmissionsTable
            project={project}
            selectedVersions={selectedVersions}
            tableType={TableType.Recurring}
            formatter={formatEmissionPerYear}
            unit={emissionUnit}
            createPrefilledEmission={createPrefilledEmission}
            updateEmission={updateEmission}
          />
        </AccordionDetails>
      </Accordion>
    </>
  )
}
